/**
 * 내 단어장 목록·상세(총괄 DEV-I-013, 디자인 단어장 v0.1). 상태는 App이 갖고, 이 함수는 상태로 화면 하나를 만든다.
 *  - 한 단(최대 720px). 상세는 별도 화면(넓은 화면에서도 두 단 없음)
 *  - 목록 행 = wordId 하나(같은 표제어도 합치지 않음). 출처 카드 = sourceId 하나. 순서는 API 그대로
 *  - 정답 보기·해설·승인 정보·내부 식별자(wordId·sourceId·attemptId)는 보이지 않고, 원 문제로 가는 링크도 없다
 *  - 수집·편집·삭제·암기 체크·시험·복습 일정·알림 단추 없음. '새 단어'·읽음 표시 없음
 *  - 확인 실패·시간 초과면 지난 단어·뜻·출처를 숨기고 빈 단어장과 구분한다
 */
import { formChips, formLine, sourceCountLabel, type WordItem } from '../core/words-model.js';
import { h } from './dom.js';

export type WordsPhase = 'loading' | 'ok' | 'error' | 'denied' | 'notfound';
interface Base {
  phase: WordsPhase;
  errorCause: string | null;
  busy: boolean;
  seq: number;
  checkedAt: number | null;
  /** 이 화면에서 한 번이라도 받았는가(오류 제목: 불러오지 못함 / 다시 확인하지 못함) */
  everLoaded: boolean;
}
export interface WordsListState extends Base {
  readonly view: 'list';
  words: WordItem[] | null;
  /** 상세에서 돌아왔을 때 초점을 돌릴 행(첫 조회 뒤 한 번) */
  focusWordId: string | null;
  /** (DEV-I-014 U-1) 지금 조회가 재확인인가(성공 뒤 빠진 초점을 '다시 확인'으로 돌림) */
  recheck: boolean;
}
export interface WordDetailState extends Base {
  readonly view: 'detail';
  readonly wordId: string;
  word: WordItem | null;
}
export type WordsState = WordsListState | WordDetailState;

const base = (): Base => ({ phase: 'loading', errorCause: null, busy: false, seq: 0, checkedAt: null, everLoaded: false });
export const blankWordList = (focusWordId: string | null): WordsListState => ({ view: 'list', ...base(), words: null, focusWordId, recheck: false });
export const blankWordDetail = (wordId: string): WordDetailState => ({ view: 'detail', ...base(), wordId, word: null });

export interface WordsHandlers {
  readonly refresh: () => void;
  readonly toList: () => void;
  readonly openWord: (wordId: string) => void;
  readonly backToWords: () => void;
  readonly openQuiz: () => void;
}

const hhmm = (t: number) => { const d = new Date(t); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };

/** (DEV-I-014 D-2) 제목은 최초·재조회와 관계없이 하나. 본문은 오류 종류별 */
function errorText(S: Base, what: '단어장' | '단어'): [string, string] {
  const title = what === '단어장' ? '단어장을 불러오지 못했어요.' : '단어를 불러오지 못했어요.';
  const body = S.errorCause === 'timeout' ? '정해진 시간 안에 받아 오지 못했어요. 다시 확인해 주세요.'
    : S.errorCause === 'network' ? '연결을 확인한 뒤 다시 확인해 주세요.'
      : '다시 확인해 주세요.'; // 서버 오류·응답 해석 불가·상세 wordId 불일치
  return [title, body];
}

function checkRow(S: Base, testid: string, H: WordsHandlers): HTMLElement {
  return h('div', { class: 'rcheck wcheck' },
    h('span', { 'data-testid': 'words-checked-at' }, S.checkedAt !== null && S.phase === 'ok' ? `확인한 시각 ${hhmm(S.checkedAt)} (이 기기 시계)` : ''),
    h('button', { class: 'secondary', 'data-testid': testid, disabled: S.busy, onclick: H.refresh }, S.busy ? '확인하는 중' : '다시 확인'));
}

function stateBox(S: Base, what: '단어장' | '단어', retryId: string, H: WordsHandlers): HTMLElement | null {
  if (S.phase === 'ok') return null;
  if (S.phase === 'loading') return h('p', { class: 'loading', role: 'status', 'data-testid': 'words-loading' }, what === '단어장' ? '단어장을 불러오고 있어요' : '단어를 불러오고 있어요');
  if (S.phase === 'notfound') {
    return h('section', { class: 'rstat', 'data-k': 'pending', 'data-testid': 'words-state', 'data-state': 'notfound', role: 'status' },
      h('h2', { 'data-testid': 'words-state-title', tabindex: '-1' }, '이 단어를 지금은 볼 수 없어요.'));
  }
  if (S.phase === 'denied') {
    return h('section', { class: 'rstat', 'data-k': 'error', 'data-testid': 'words-state', 'data-state': 'denied', role: 'alert' },
      // (DEV-I-014 D-1) 제목만. 원인 단정·추가 본문 없음
      h('h2', { 'data-testid': 'words-state-title', tabindex: '-1' }, what === '단어장' ? '단어장을 볼 수 없어요.' : '이 단어를 볼 수 없어요.'));
  }
  const [t, b] = errorText(S, what);
  return h('section', { class: 'rstat', 'data-k': 'error', 'data-testid': 'words-state', 'data-state': 'error', 'data-cause': S.errorCause ?? '', role: 'alert' },
    h('h2', { 'data-testid': 'words-state-title', tabindex: '-1' }, t), h('p', { 'data-testid': 'words-state-body' }, b),
    h('div', { class: 'rcheck' }, h('button', { class: 'secondary', 'data-testid': retryId, disabled: S.busy, onclick: H.refresh }, S.busy ? '확인하는 중' : '다시 확인')));
}

const chips = (w: WordItem, testid: string) => {
  const forms = formChips(w);
  return forms.length === 0 ? null : h('span', { class: 'wforms', 'data-testid': testid }, ...forms.map((f) => h('span', { class: 'chip wform', lang: 'en' }, f)));
};

export function renderWordList(S: WordsListState, H: WordsHandlers): HTMLElement {
  const root = h('div', { class: 'list-main words', 'data-testid': 'words', 'data-view': 'list', 'data-phase': S.phase });
  // DEV-I-020: 단어 시험 진입. 단어장이 비어 있어도 진입점은 그대로(시작 응답이 이유를 알려 준다)
  root.append(h('div', { class: 'lhead' }, h('h2', { 'data-testid': 'words-title', tabindex: '-1' }, '내 단어장'),
    h('button', { class: 'secondary', 'data-testid': 'open-quiz', onclick: H.openQuiz }, '단어 시험')));
  const box = stateBox(S, '단어장', 'words-retry', H);
  if (box !== null) root.append(box);
  if (S.phase === 'ok' && S.words !== null) {
    root.append(checkRow(S, 'words-refresh', H));
    if (S.busy) root.append(h('p', { class: 'wbusy', role: 'status', 'data-testid': 'words-busy' }, '확인하는 중이에요. 확인이 끝나면 단어를 열 수 있어요.'));
    if (S.words.length === 0) {
      root.append(h('p', { class: 'wempty', 'data-testid': 'words-empty', tabindex: '-1' }, '현재 표시할 단어가 없어요.'));
    } else {
      root.append(h('ul', { class: 'wlist', 'data-testid': 'word-list', 'aria-busy': S.busy ? 'true' : 'false' }, ...S.words.map((w) => {
        const count = sourceCountLabel(w.sources.length, 'list');
        return h('li', {}, h('button', {
          class: 'wrow', 'data-testid': `wrow-${w.wordId}`, 'data-word': w.wordId, disabled: S.busy,
          onclick: () => { if (!S.busy) H.openWord(w.wordId); },
        },
        h('span', { class: 'whw', lang: 'en', 'data-testid': 'wrow-headword' }, w.headword),
        h('span', { class: 'wmn', 'data-testid': 'wrow-meaning' }, w.meaning),
        h('span', { class: 'wmeta' }, chips(w, 'wrow-forms'), count !== null ? h('span', { class: 'wcount', 'data-testid': 'wrow-count' }, count) : null)));
      })));
    }
  }
  root.append(h('div', { class: 'wfoot' }, h('button', { class: 'secondary', 'data-testid': 'to-list', onclick: H.toList }, '과제 목록으로')));
  return root;
}

export function renderWordDetail(S: WordDetailState, H: WordsHandlers): HTMLElement {
  const root = h('div', { class: 'list-main words', 'data-testid': 'words', 'data-view': 'detail', 'data-phase': S.phase });
  const box = stateBox(S, '단어', 'wd-retry', H);
  if (box !== null) root.append(box);
  if (S.phase === 'ok' && S.word !== null) {
    const w = S.word;
    root.append(h('section', { class: 'whead', 'data-testid': 'wd-head' },
      h('h2', { lang: 'en', 'data-testid': 'wd-headword', tabindex: '-1' }, w.headword),
      h('p', { class: 'wmeaning' }, h('span', { class: 'wlab' }, '의미'), h('span', { 'data-testid': 'wd-meaning' }, w.meaning)),
      h('div', { class: 'wmeta' }, chips(w, 'wd-forms'), h('span', { class: 'wcount', 'data-testid': 'wd-count' }, sourceCountLabel(w.sources.length, 'detail')!))));
    root.append(checkRow(S, 'wd-refresh', H));
    root.append(h('h3', { class: 'wsub' }, '출처'));
    for (const s of w.sources) {
      root.append(h('section', { class: 'wsrc', 'data-testid': `wsrc-${s.sourceId}`, 'data-source': s.sourceId },
        h('p', { class: 'wsform', 'data-testid': 'wsrc-form', 'data-line': formLine(s, w.headword) },
          '고른 보기 형태 ', h('b', { lang: 'en' }, s.form), s.form === w.headword ? null : ' → 표제어 ', s.form === w.headword ? null : h('b', { lang: 'en' }, w.headword)),
        h('dl', { class: 'info' },
          h('dt', {}, '문맥 품사'), h('dd', { 'data-testid': 'wsrc-pos' }, s.posInContext),
          h('dt', {}, '문맥 뜻'), h('dd', { 'data-testid': 'wsrc-meaning' }, s.meaningInContext),
          h('dt', {}, '과제'), h('dd', { 'data-testid': 'wsrc-assignment' }, s.origin.assignmentTitle),
          h('dt', {}, '문제지'), h('dd', { 'data-testid': 'wsrc-release' }, s.origin.releaseTitle),
          h('dt', {}, '위치'), h('dd', { 'data-testid': 'wsrc-point' }, `${s.origin.displayNumber}번 지점`))));
    }
  }
  root.append(h('div', { class: 'wfoot' }, h('button', { class: 'secondary', 'data-testid': 'words-back', onclick: H.backToWords }, '목록으로')));
  return root;
}
